/*
 * UserForm.tsx
 * Un formulaire pour ajouter ou modifier un utilisateur.
 * Utilise des hooks personnalisés pour gérer l'état et les interactions.
 * Affiche des champs pour les informations utilisateur, les rôles.
 */

// Import React
import React, { useState } from 'react';

// Import Radix
import * as Tabs from '@radix-ui/react-tabs';

// Import Communs
import {
	InputField,
	SelectField,
	SecondaryButton,
	FormSaveActions,
} from '../../../common';
import { getApiBaseUrl } from 'common/services/apiBaseUrl';
const apiBaseUrl = getApiBaseUrl();
export const API_URL = apiBaseUrl + '/v2';
import apiFetch from 'common/services/apiFetch';

// Import User
import { roleOptions, statusOptions } from '../types/types';
import { useUserForm } from '../hooks/useUserForm';
import { UserRightsMatrix } from '../../permissions';

// Composant UserForm
const UserForm: React.FC = () => {
	// Custom hook to manage user form state and logic
	const {
		user,
		loading,
		passwordError,
		isDirty,
		isSubmitting,
		handleChange,
		handleChangeSelect,
		handleSubmit,
		handleSave,
		navigate,
		id,
		setUser,
	} = useUserForm();

	// Hook de state
	const [showPasswordPopup, setShowPasswordPopup] = useState(false);
	const [newPassword, setNewPassword] = useState('');
	const [error, setError] = useState('');

	// Mapping : traduction des status
	const statusLabels: Record<string, string> = {
		active: 'Actif',
		inactive: 'Inactif',
		suspended: 'Suspendu',
	};

	// Show a loading indicator if data is being fetched
	if (loading) {
		return <div>Loading...</div>;
	}

	// Gestion de la soumission du formulaire
	const handleFormSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		await handleSubmit(e);
	};

	// Gestion de la régénération du mot de passe
	function handleRegeneratePassword() {
		setShowPasswordPopup(true);
		setNewPassword('');
		setError('');
	}

	// Gestion de la soumission du nouveau mot de passe
	function handleSubmitPassword() {
		// Critères classiques
		if (newPassword.length < 16) {
			setError('Le mot de passe doit faire au moins 16 caractères.');
			return;
		}
		if (!/[A-Z]/.test(newPassword)) {
			setError('Le mot de passe doit contenir au moins une majuscule.');
			return;
		}
		if (!/[a-z]/.test(newPassword)) {
			setError('Le mot de passe doit contenir au moins une minuscule.');
			return;
		}
		if (!/[0-9]/.test(newPassword)) {
			setError('Le mot de passe doit contenir au moins un chiffre.');
			return;
		}
		if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword)) {
			setError('Le mot de passe doit contenir au moins un caractère spécial.');
			return;
		}

		// Appel API pour changer le mot de passe
		const token = localStorage.getItem('token') || '';
		apiFetch(`${API_URL}/users/${id}/regenerate-password`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({ password: newPassword }),
		}).then(async (res) => {
			if (!res.ok) {
				const data = await res.json();
				setError(data.error || 'Erreur inconnue');
				return;
			}
			setShowPasswordPopup(false);
			alert('Mot de passe mis à jour.');
		});
	}

	return (
		<div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
			<div className="flex items-center space-x-4 mb-6">
				{/* Avatar rond */}
				<div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-xl font-bold">
					{id ? (user.firstName?.[0] ?? '') + (user.name?.[0] ?? '') : 'new'}
				</div>

				{/* Nom et info */}
				<div className="flex flex-col">
					<span className="text-xl font-semibold">
						{/* Editing user */}
						{id ? (
							<>
								{user.firstName} {user.name}
							</>
						) : (
							<>Nouvel utilisateur</>
						)}
					</span>
					<span className="text-sm text-gray-500 flex items-center space-x-2">
						<span>{user.status ? statusLabels[user.status] : 'inconnu'}</span>
						<span
							className={`w-3 h-3 rounded-full ${user.status === 'active' ? 'bg-green-500' : user.status === 'inactive' ? 'bg-orange-500' : 'bg-red-500'}`}
						></span>
					</span>
				</div>
			</div>

			<Tabs.Root defaultValue="profile">
				<Tabs.List className="flex border-b mb-4">
					<Tabs.Trigger
						value="profile"
						className="px-4 py-2 font-semibold data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600"
					>
						Profil
					</Tabs.Trigger>
					<Tabs.Trigger
						value="rights"
						className="px-4 py-2 font-semibold data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600"
					>
						Droits d’accès
					</Tabs.Trigger>
				</Tabs.List>

				<form onSubmit={handleFormSubmit} className="space-y-4">
					{/* Onglet Profil */}
					<Tabs.Content value="profile" className="space-y-4">
						{/* Login + bouton régénérer mot de passe */}
						<div className="grid grid-cols-2 gap-4">
							{/* Login field */}
							<InputField
								label="Login"
								name="login"
								value={user.login}
								onChange={handleChange}
								placeholder="a.dupont"
							/>

							{/* Email field */}
							<InputField
								label="Email"
								name="email"
								type="email"
								value={user.email}
								onChange={handleChange}
								placeholder="a.dupont@example.com"
							/>
						</div>
						<div className="grid grid-cols-2 gap-4">
							{/* First name field */}
							<InputField
								label="Prénom"
								name="firstName"
								value={user.firstName}
								onChange={handleChange}
								placeholder="Alice"
							/>
							{/* Last name field */}
							<InputField
								label="Nom"
								name="name"
								value={user.name}
								onChange={handleChange}
								placeholder="Dupont"
							/>
						</div>
						<div className="grid grid-cols-2 gap-4">
							{/* State field */}
							<SelectField
								label="État"
								selectedKey={user.status}
								onValueChange={(val) =>
									handleChangeSelect({
										target: {
											name: 'status',
											value: val,
										},
									} as React.ChangeEvent<HTMLSelectElement>)
								}
								options={statusOptions}
								loading={loading}
							/>

							{/* Password field, only show if creating a new user */}
							{!id && (
								<InputField
									label="Mot de passe"
									name="password"
									type="password"
									value={user.password}
									onChange={handleChange}
									error={passwordError}
									placeholder="••••••••"
								/>
							)}
						</div>
					</Tabs.Content>
					{/* Onglet Droits d’accès */}
					<Tabs.Content value="rights">
						<div className="grid grid-cols-2 gap-4">
							<SelectField
								label="Rôle"
								selectedKey={user.roles ? user.roles[0] : ''}
								onValueChange={(val) =>
									handleChangeSelect({
										target: {
											name: 'roles',
											value: val,
										},
									} as React.ChangeEvent<HTMLSelectElement>)
								}
								options={roleOptions}
								loading={loading}
							/>
						</div>
						<div className="mt-4">
							<UserRightsMatrix
								initialPermissions={user.permissions ?? []}
								onChange={(perms) =>
									setUser((prev) => ({ ...prev, permissions: perms }))
								}
							/>
						</div>
					</Tabs.Content>
				</form>

				{/* Form actions */}
				<FormSaveActions
					className="flex justify-end mt-4 space-x-2"
					isDirty={isDirty}
					isSubmitting={isSubmitting}
					onCancel={() => navigate('/app/users')}
					onSave={() => handleSave(false)}
					onSaveAndLeave={() => handleSave(true)}
					extraActions={
						id ? (
							<SecondaryButton
								onClick={handleRegeneratePassword}
								label="Régénérer mot de passe"
								size="lg"
							/>
						) : undefined
					}
				/>
			</Tabs.Root>

			{showPasswordPopup && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-md">
						<h3 className="text-lg font-semibold mb-4">Nouvel mot de passe</h3>
						<input
							type="password"
							value={newPassword}
							onChange={(e) => setNewPassword(e.target.value)}
							className="w-full border border-gray-300 rounded-md p-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
							autoFocus
						/>
						{error && <p className="text-red-500 mb-4">{error}</p>}
						<div className="flex justify-end gap-2">
							<button
								onClick={handleSubmitPassword}
								className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition"
							>
								Valider
							</button>
							<button
								onClick={() => setShowPasswordPopup(false)}
								className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400 transition"
							>
								Annuler
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default UserForm;
